import { useCallback, useSyncExternalStore } from "react";

/**
 * Which layout the user is looking at.
 *
 * "simple" mirrors the mobile app — one card per fact, a four-slot sidebar
 * with a "Más" catch-all, detail behind progressive disclosure. "advanced" is
 * the historical dense layout.
 */
export type DashboardView = "simple" | "advanced";

const VIEW_STORAGE_KEY = "fiscalo:dashboard-view";

/**
 * Everyone — new and existing — lands on the simple view. The density is the
 * minority preference, so users who want it flip once and it sticks; the
 * one-time hint next to the toggle is what makes that discoverable.
 */
const DEFAULT_VIEW: DashboardView = "simple";

function readStoredView(): DashboardView | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(VIEW_STORAGE_KEY);
    if (raw === "simple" || raw === "advanced") return raw;
    return null;
  } catch {
    return null;
  }
}

// ── Shared store ────────────────────────────────────────────────────────────
// The preference drives BOTH the dashboard and the sidebar, which are separate
// component trees. Plain useState per call site gave each one its own copy, so
// toggling in the dashboard header left the sidebar on the old layout until a
// reload. One module-level value + subscription keeps every consumer in step.

let currentView: DashboardView = readStoredView() ?? DEFAULT_VIEW;
let userHasChosen: boolean = readStoredView() != null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab changing the preference should move this one too — same
  // rationale as the in-page subscription, one step wider.
  const onStorage = (e: StorageEvent): void => {
    if (e.key !== VIEW_STORAGE_KEY) return;
    const next = readStoredView();
    if (next && next !== currentView) {
      currentView = next;
      userHasChosen = true;
      emit();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function setStoredView(next: DashboardView): void {
  if (next === currentView && userHasChosen) return;
  currentView = next;
  userHasChosen = true;
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, next);
  } catch {
    // Storage unavailable — keep the in-memory choice for this session.
  }
  emit();
}

function getViewSnapshot(): DashboardView {
  return currentView;
}

function getChosenSnapshot(): boolean {
  return userHasChosen;
}

// Server snapshots: no localStorage during SSR/prerender, so report defaults.
function getServerView(): DashboardView {
  return DEFAULT_VIEW;
}

function getServerChosen(): boolean {
  return false;
}

export interface UseDashboardViewResult {
  view: DashboardView;
  setView: (next: DashboardView) => void;
  /** True until the user has explicitly picked a view on this device. */
  isDefaulted: boolean;
}

/**
 * Layout preference, persisted per-device in localStorage and shared across
 * every consumer in the tree. No backend involvement, so the choice does not
 * follow the user to another browser.
 */
export function useDashboardView(): UseDashboardViewResult {
  const view = useSyncExternalStore(subscribe, getViewSnapshot, getServerView);
  const hasChosen = useSyncExternalStore(
    subscribe,
    getChosenSnapshot,
    getServerChosen,
  );

  const setView = useCallback((next: DashboardView) => {
    setStoredView(next);
  }, []);

  return { view, setView, isDefaulted: !hasChosen };
}
